// frontend/src/components/league/LeagueTopTabs.tsx
//
// League tabs FOR THE GLOBAL TOP NAV (desktop). Owner call 2026-07-10: the
// bespoke in-league header (league name + second nav row) is dead - the
// league name only earns space where you CHOOSE a league (dashboard) and on
// the League home page itself. In-league, the top bar becomes:
//   [FANTASY FUTBOL]   My Team  Matchup  League  Players   [profile icon]
//
// Self-contained: reads the same cached queries LeagueLayout uses (league
// details for pre-start tab variants, unread count for the League badge), so
// mounting this in the global chrome costs no extra requests.
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useLeagueDetails } from '@/hooks/useLeagueDetails';
import { fetchUnreadActivityCount } from '@/api/activity';

const LeagueTopTabs: React.FC<{ leagueId: number }> = ({ leagueId }) => {
  const { user, token } = useAuth();
  const location = useLocation();
  const { data: leagueDetails } = useLeagueDetails(leagueId);

  // Same query key + cadence as LeagueLayout's bottom nav - deduped by cache.
  const { data: unreadData } = useQuery({
    queryKey: ['unreadActivityCount', leagueId],
    queryFn: () => fetchUnreadActivityCount(leagueId, token!),
    enabled: !!token,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const unreadCount = (unreadData?.unread_count || 0) + (unreadData?.new_players_unread_count || 0);

  if (!leagueDetails) return null;

  const isPreStartPhase =
    leagueDetails.draft_status === 'PRE_DRAFT' ||
    leagueDetails.draft_status === 'ROSTER_INPUT_ACTIVE' ||
    leagueDetails.draft_status === 'ROSTERS_SET';
  const isCommissioner = !!user && leagueDetails.owner_id === user.id;
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isOnCommishHQ = normalizedPathname === `/leagues/${leagueId}/commish`;
  // Members see the same page read-only at /settings; both keep League lit.
  const isOnLeagueMode = isOnCommishHQ || normalizedPathname === `/leagues/${leagueId}/settings`;

  // Non-members can load league details (invite previews) but every tab
  // behind these links 403s - show no tabs instead of dead ends.
  const isMember =
    isCommissioner || !!leagueDetails.teams?.some(t => t.owner_id === user?.id);
  if (!isMember) return null;

  // Same composition rules as the mobile bottom nav (LeagueLayout).
  const navItems: { path: string; label: string; key: string }[] = [];
  if (isPreStartPhase) {
    if (isCommissioner) {
      navItems.push({ path: `/leagues/${leagueId}/setup`, label: 'League Setup', key: 'setup' });
    }
    navItems.push({ path: `/leagues/${leagueId}/my-team`, label: 'My Team', key: 'my-team' });
    if (isCommissioner) {
      navItems.push({ path: `/leagues/${leagueId}/players`, label: 'Players', key: 'players' });
    }
  } else {
    navItems.push({ path: `/leagues/${leagueId}/my-team`, label: 'My Team', key: 'my-team' });
    navItems.push({ path: `/leagues/${leagueId}/matchup`, label: 'Matchup', key: 'matchup' });
    navItems.push({ path: `/leagues/${leagueId}/league`, label: 'League', key: 'league' });
    navItems.push({ path: `/leagues/${leagueId}/players`, label: 'Players', key: 'players' });
  }

  return (
    <nav className="flex items-center gap-1 lg:gap-2" aria-label="League navigation">
      {navItems.map(item => (
        <div key={item.key} className="relative">
          <NavLink
            to={item.path}
            className={({ isActive }) => {
              const isModeActive = isActive || (
                isOnLeagueMode && (
                  (isPreStartPhase && item.key === 'setup')
                  || (!isPreStartPhase && item.key === 'league')
                )
              );
              return `font-heading font-bold text-sm px-3 py-2 rounded-md transition-colors duration-150 whitespace-nowrap ${
                isModeActive
                  ? 'bg-accent-yellow text-primary shadow-sm'
                  : 'text-accent-light-blue hover:bg-primary/50 hover:text-accent-yellow'
              }`;
            }}
          >
            {item.label}
          </NavLink>
          {item.key === 'league' && unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-white text-badge font-bold pointer-events-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
      ))}
    </nav>
  );
};

export default LeagueTopTabs;
